import ArrowBack from '@mui/icons-material/ArrowBack'
import Download from '@mui/icons-material/Download'
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { getFinancialTransactionReportLookup, getFinancialTransactionsAddedAfterInvoicing, downloadFinancialTransactionsAddedAfterInvoicing, FinancialTransactionReportFilters, FinancialTransactionReportParameters } from 'controllers/reports'
import { getChargeTypes } from 'controllers/invoicing/ChargeTypesController'
import { useAuth } from 'context'
import { ChargeTypeDto } from 'dtos/invoicing'
import { FinancialTransactionAddedAfterInvoicingReportDto, REPORT_SPECIAL_IDENTIFIERS } from 'dtos'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { SortableTableCell } from 'components'
import { DateField } from 'components/fields'
import { formatCurrency, formatDateTime } from 'utils'
import { enqueueSnackbar } from 'notistack'

type LookupField = 'importerStockNumber' | 'vin' | 'year' | 'make' | 'model'

interface DraftFilters {
  transactionDateFrom: string
  transactionDateTo: string
  importerStockNumber: string
  chargeTypeId?: number
  year: string
  make: string
  model: string
  vin: string
}

const initialFilters: DraftFilters = {
  transactionDateFrom: '',
  transactionDateTo: '',
  importerStockNumber: '',
  chargeTypeId: undefined,
  year: '',
  make: '',
  model: '',
  vin: ''
}

const reportPageSizeOptions = [10, 25, 50, 100]

export default function FinancialTransactionsAddedAfterInvoicingReport() {
  const auth = useAuth()
  const navigate = useNavigate()
  const [draftFilters, setDraftFilters] = useState<DraftFilters>(initialFilters)
  const [appliedFilters, setAppliedFilters] = useState<FinancialTransactionReportFilters | null>(null)
  const [rows, setRows] = useState<FinancialTransactionAddedAfterInvoicingReportDto[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [orderBy, setOrderBy] = useState('transactionCreatedDateTime')
  const [order, setOrder] = useState<'asc' | 'desc'>('desc')
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [hasRun, setHasRun] = useState(false)
  const [dateError, setDateError] = useState('')
  const [chargeTypes, setChargeTypes] = useState<ChargeTypeDto[]>([])
  const [lookupOptions, setLookupOptions] = useState<Record<LookupField, string[]>>({
    importerStockNumber: [],
    vin: [],
    year: [],
    make: [],
    model: []
  })
  const requestNumber = useRef(0)
  const lastRequestSignature = useRef('')

  const authorized = auth?.status === 'authorized' &&
    auth.reports.some(report => report.specialIdentifier === REPORT_SPECIAL_IDENTIFIERS.FINANCIAL_TRANSACTIONS_ADDED_AFTER_INVOICING_REPORT)

  useEffect(() => {
    if (auth?.status !== 'authorized') {
      return
    }

    getChargeTypes().then(response => setChargeTypes(response.value))
    ;(['importerStockNumber', 'vin', 'year', 'make', 'model'] as LookupField[]).forEach(field => loadLookup(field))
  }, [auth?.status])

  const loadLookup = (field: LookupField, search = '', filters = draftFilters) => {
    getFinancialTransactionReportLookup({
      lookupField: field,
      lookupSearch: search,
      year: filters.year ? Number(filters.year) : undefined,
      make: filters.make || undefined,
      model: filters.model || undefined
    })
      .then(response => {
        setLookupOptions(previous => ({ ...previous, [field]: response.value.values }))
      })
      .catch(() => {
        setLookupOptions(previous => ({ ...previous, [field]: [] }))
      })
  }

  const requestResults = async (
    filters: FinancialTransactionReportFilters,
    nextPage: number,
    nextPageSize: number,
    nextOrderBy: string,
    nextOrder: 'asc' | 'desc'
  ) => {
    lastRequestSignature.current = JSON.stringify({ filters, nextPage, nextPageSize, nextOrderBy, nextOrder })
    const currentRequest = ++requestNumber.current
    setIsLoading(true)

    try {
      const response = await getFinancialTransactionsAddedAfterInvoicing({
        ...filters,
        page: nextPage,
        pageSize: nextPageSize,
        orderBy: nextOrderBy,
        order: nextOrder
      })

      if (currentRequest !== requestNumber.current) {
        return false
      }

      setRows(response.value)
      setTotalCount(response.totalCount ?? 0)
      return true
    } catch {
      if (currentRequest === requestNumber.current) {
        enqueueSnackbar('Unable to load the financial transactions report.', { variant: 'error' })
      }
      return false
    } finally {
      if (currentRequest === requestNumber.current) {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    if (appliedFilters && hasRun) {
      const signature = JSON.stringify({ filters: appliedFilters, nextPage: page, nextPageSize: pageSize, nextOrderBy: orderBy, nextOrder: order })
      if (signature !== lastRequestSignature.current) {
        requestResults(appliedFilters, page, pageSize, orderBy, order)
      }
    }
  }, [appliedFilters, hasRun, page, pageSize, orderBy, order])

  if (!auth || auth.status === 'loading') {
    return <CircularProgress />
  }

  if (auth.status === 'unauthorized' || !authorized) {
    return <Navigate replace to='/reports' />
  }

  const runReport = async () => {
    if (draftFilters.transactionDateFrom && draftFilters.transactionDateTo &&
        draftFilters.transactionDateFrom > draftFilters.transactionDateTo) {
      setDateError('Transaction Date From cannot be later than Transaction Date To.')
      return
    }

    setDateError('')
    const filters: FinancialTransactionReportFilters = {
      transactionDateFrom: draftFilters.transactionDateFrom || undefined,
      transactionDateTo: draftFilters.transactionDateTo || undefined,
      importerStockNumber: draftFilters.importerStockNumber || undefined,
      chargeTypeId: draftFilters.chargeTypeId,
      year: draftFilters.year ? Number(draftFilters.year) : undefined,
      make: draftFilters.make || undefined,
      model: draftFilters.model || undefined,
      vin: draftFilters.vin || undefined
    }

    const successful = await requestResults(filters, 0, pageSize, 'transactionCreatedDateTime', 'desc')
    if (successful) {
      setPage(0)
      setOrderBy('transactionCreatedDateTime')
      setOrder('desc')
      setAppliedFilters(filters)
      setHasRun(true)
    }
  }

  const clearReport = () => {
    requestNumber.current += 1
    setDraftFilters(initialFilters)
    setAppliedFilters(null)
    setRows([])
    setTotalCount(0)
    setPage(0)
    setPageSize(10)
    setOrderBy('transactionCreatedDateTime')
    setOrder('desc')
    setHasRun(false)
    setDateError('')
  }

  const handleSort = (property: string) => {
    setPage(0)
    setOrder(previous => orderBy === property && previous === 'asc' ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleLookupChange = (field: LookupField, value: string) => {
    setDraftFilters(previous => {
      const next = { ...previous, [field]: value }
      if (field === 'year') {
        next.make = ''
        next.model = ''
      }
      if (field === 'make') {
        next.model = ''
      }
      return next
    })

    if (field === 'year' || field === 'make') {
      loadLookup('model', '', { ...draftFilters, [field]: value, ...(field === 'year' ? { make: '' } : {}) })
    }
  }

  const reportParameters = appliedFilters
    ? {
        ...appliedFilters,
        page,
        pageSize,
        orderBy,
        order
      }
    : null

  const downloadCsv = async () => {
    if (!reportParameters) {
      return
    }

    setIsDownloading(true)
    try {
      await downloadFinancialTransactionsAddedAfterInvoicing(reportParameters as FinancialTransactionReportParameters)
    } catch {
      enqueueSnackbar('Unable to download the financial transactions report.', { variant: 'error' })
    } finally {
      setIsDownloading(false)
    }
  }

  const autocomplete = (field: LookupField, label: string, id: string, value: string, onChange: (value: string) => void) => (
    <Autocomplete
      id={id}
      options={lookupOptions[field]}
      value={value || null}
      onChange={(_, nextValue) => onChange(nextValue ?? '')}
      onInputChange={(_, inputValue, reason) => {
        if (reason === 'input') {
          loadLookup(field, inputValue)
        }
      }}
      isOptionEqualToValue={(option, selected) => option === selected}
      renderInput={params => <TextField {...params} id={`${id}-input`} label={label} />}
    />
  )

  return (
    <Grid container spacing={1} alignItems='center'>
      <Grid item xs={12}>
        <Button id='financial-report-back' variant='text' startIcon={<ArrowBack />} onClick={() => navigate('/reports')}>
          Reports
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Typography variant='h4'>Financial Transactions Added After Invoicing</Typography>
        <Typography variant='body1'>
          Displays financial transactions that were added to vehicle records after the vehicle was invoiced.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Alert severity='info'>
          This report shows transactions with a transaction date and time that is after the vehicle&apos;s invoice date.
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography fontWeight='bold' variant='h5' gutterBottom>Filters</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <DateField name='financial-report-transaction-date-from' label='Transaction Date From' clearable value={draftFilters.transactionDateFrom} onChange={value => setDraftFilters(previous => ({ ...previous, transactionDateFrom: value ?? '' }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DateField name='financial-report-transaction-date-to' label='Transaction Date To' clearable value={draftFilters.transactionDateTo} onChange={value => setDraftFilters(previous => ({ ...previous, transactionDateTo: value ?? '' }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {autocomplete('importerStockNumber', 'Importer Stock #', 'financial-report-importer-stock-number', draftFilters.importerStockNumber, value => setDraftFilters(previous => ({ ...previous, importerStockNumber: value })))}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  id='financial-report-transaction-type'
                  options={chargeTypes}
                  value={chargeTypes.find(chargeType => chargeType.chargeTypeId === draftFilters.chargeTypeId) ?? null}
                  onChange={(_, value) => setDraftFilters(previous => ({ ...previous, chargeTypeId: value?.chargeTypeId }))}
                  getOptionLabel={option => option.name}
                  isOptionEqualToValue={(option, value) => option.chargeTypeId === value.chargeTypeId}
                  renderInput={params => <TextField {...params} id='financial-report-transaction-type-input' label='Transaction Type' />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {autocomplete('year', 'Year', 'financial-report-year', draftFilters.year, value => handleLookupChange('year', value))}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {autocomplete('make', 'Make', 'financial-report-make', draftFilters.make, value => handleLookupChange('make', value))}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {autocomplete('model', 'Model', 'financial-report-model', draftFilters.model, value => handleLookupChange('model', value))}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {autocomplete('vin', 'VIN', 'financial-report-vin', draftFilters.vin, value => setDraftFilters(previous => ({ ...previous, vin: value })))}
              </Grid>
              {dateError && <Grid item xs={12}><Alert severity='error'>{dateError}</Alert></Grid>}
              <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
                <Button id='financial-report-clear' variant='text' onClick={clearReport}>Clear</Button>
                <Button id='financial-report-run' variant='contained' onClick={runReport} disabled={isLoading}>
                  {isLoading ? <CircularProgress size={20} /> : 'RUN REPORT'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container alignItems='center' justifyContent='space-between' spacing={1}>
              <Grid item><Typography fontWeight='bold' variant='h5'>Results ({totalCount})</Typography></Grid>
              <Grid item>
                <Button id='financial-report-download-csv' variant='text' startIcon={<Download />} onClick={downloadCsv} disabled={!hasRun || isDownloading}>
                  {isDownloading ? <CircularProgress size={20} /> : 'Download CSV'}
                </Button>
              </Grid>
            </Grid>

            {!hasRun && <Alert severity='info'>Select your filters and click Run Report to view financial transactions added after invoicing.</Alert>}
            {hasRun && !isLoading && rows.length === 0 && <Alert severity='info'>No financial transactions were found matching the selected filters.</Alert>}
            {isLoading && <Grid container justifyContent='center' sx={{ py: 3 }}><CircularProgress /></Grid>}
            {!isLoading && rows.length > 0 && (
              <>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        {[
                          ['Importer Stock #', 'importerStockNumber'],
                          ['VIN', 'vin'],
                          ['Year', 'year'],
                          ['Make', 'make'],
                          ['Model', 'model'],
                          ['Invoice Date', 'invoicedDateTime'],
                          ['Transaction Date/Time', 'transactionCreatedDateTime'],
                          ['Transaction Type', 'transactionType'],
                          ['Description', 'description'],
                          ['Transaction Amount', 'amount']
                        ].map(([label, property]) => (
                          <SortableTableCell
                            align={property === 'amount' ? 'right' : 'left'}
                            key={property}
                            propertyName={property}
                            sortBy={orderBy}
                            sortDirection={order}
                            onChangeSortByDirection={handleSort}
                          >
                            {label}
                          </SortableTableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow key={row.vehicleTransactionId}>
                          <TableCell>{row.importerStockNumber || '-'}</TableCell>
                          <TableCell>{row.vin}</TableCell>
                          <TableCell>{row.year}</TableCell>
                          <TableCell>{row.make}</TableCell>
                          <TableCell>{row.model}</TableCell>
                          <TableCell>{new Date(row.invoicedDateTime).toLocaleDateString('en-US')}</TableCell>
                          <TableCell>{formatDateTime(row.transactionCreatedDateTime)}</TableCell>
                          <TableCell>{row.transactionType}</TableCell>
                          <TableCell>{row.description || '-'}</TableCell>
                          <TableCell align='right'>{formatCurrency(row.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component='div'
                  count={totalCount}
                  page={page}
                  rowsPerPage={pageSize}
                  rowsPerPageOptions={reportPageSizeOptions}
                  SelectProps={{ fullWidth: false }}
                  sx={{
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
                      fontSize: '9pt'
                    },
                    '& .MuiTablePagination-toolbar': {
                      flexWrap: 'nowrap',
                      minHeight: 40
                    }
                  }}
                  onPageChange={(_, nextPage) => setPage(nextPage)}
                  onRowsPerPageChange={event => {
                    setPageSize(Number(event.target.value))
                    setPage(0)
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}
